// Reading a metro trucking rate sheet into the rows convertMetroTariff expects.
//
// The sheets come from the carriers rather than from us, and they vary: four lines of
// preamble sit above the header, and the same column goes by several names depending on who
// filled it in and which way the freight is moving. A sheet read as though row one were the
// header produces columns called "SUPPLIER:" and nothing else matches, so the whole file
// silently yields no rates.
//
// Kept free of the rest of the app on purpose - this is pure text handling, and everything
// it would otherwise reach imports axios, which the test runner cannot load.

// Column names seen across the carriers' sheets, squashed to letters and digits so that
// spacing, punctuation and the odd typo ("DE:LIVERY LOCATION") all land on the same key.
const COLUMN_ALIASES = {
    POSTCODE: 'POSTCODE/ZIPCODE',
    POSTCODEZIPCODE: 'POSTCODE/ZIPCODE',
    ZIPCODE: 'POSTCODE/ZIPCODE',

    // Import sheets deliver to the door, export sheets collect from it. Same column.
    NAMEDLOCATION: 'NAMED LOCATION',
    DELIVERYLOCATION: 'NAMED LOCATION',
    COLLECTIONLOCATION: 'NAMED LOCATION',
    LOCATION: 'NAMED LOCATION',

    PORT: 'PORT',
    ORIGINPORT: 'PORT',
    DESTINATIONPORT: 'PORT',

    COUNTRY: 'COUNTRY',
    MODE: 'MODE',
    CURRENCY: 'CURRENCY',
    CCY: 'CURRENCY',

    MINIMUMFEE: 'MINIMUM FEE',
    MINFEE: 'MINIMUM FEE',
    MINIMUM: 'MINIMUM FEE',

    PERM3: 'PER M3',
    PERCBM: 'PER M3',
    PERTONNE: 'PER TONNE',
    PERTON: 'PER TONNE',

    PERKG: 'PER KG',
    PERCHARGEABLEWEIGHTKG: 'PER KG',
    PERCHARGEABLEKG: 'PER KG',

    "20CONTAINER": "20'",
    "20FT": "20'",
    "20": "20'",
    "40CONTAINER": "40'",
    "40FT": "40'",
    "40": "40'",

    FUELSURCHARGE: 'FUEL SURCHARGE',
    ROUTEINFORMATION: 'ROUTE INFORMATION',
    TRANSITTIME: 'TRANSIT TIME',
    TRANSITTIMEDAYS: 'TRANSIT TIME',
};

function squash(name) {
    return String(name == null ? '' : name).toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/** The name convertMetroTariff knows this column by, or the column's own name if it is new. */
export function canonicalHeader(name) {
    const key = squash(name);
    if (!key) return '';
    return COLUMN_ALIASES[key] || String(name).trim();
}

/**
 * Which row carries the column names. The preamble above it is the carrier's own header
 * block, and its length is not fixed, so the row is found by what is in it rather than by
 * counting. Returns -1 when the sheet has no recognisable header at all.
 */
export function findHeaderRow(rows) {
    const isHeader = (row) => {
        const names = (row || []).map(squash);
        return names.includes('POSTCODE') || names.includes('POSTCODEZIPCODE') || names.includes('ZIPCODE');
    };
    const at = (rows || []).findIndex(isHeader);
    return at;
}

// "01/01/26 - 31/12/26" -> { validFrom: '2026-01-01', validUntil: '2026-12-31' }, which is
// what a date input wants. Day first: these sheets are Australian and New Zealand, and
// "31/12" settles it either way.
function parseValidity(text) {
    // Pick the dates out rather than splitting on a separator: carriers annotate these
    // ("01/01/25 - 31/12/25 (per source)") and write the range with a dash, an en dash or
    // the word "to".
    const parts = String(text || '').match(/\d{1,2}\/\d{1,2}\/(?:\d{4}|\d{2})/g) || [];
    const toIso = (s) => {
        const m = /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/.exec(String(s).trim());
        if (!m) return '';
        const [, d, mo, y] = m;
        const year = y.length === 2 ? `20${y}` : y;
        const day = d.padStart(2, '0');
        const month = mo.padStart(2, '0');
        if (Number(month) > 12 || Number(day) > 31) return '';
        return `${year}-${month}-${day}`;
    };
    return { validFrom: toIso(parts[0]), validUntil: toIso(parts[1]) };
}

/**
 * The carrier's header block: everything above the column names, as "LABEL:" / value pairs.
 * Carries the supplier and the validity, which otherwise have to be typed in by hand.
 */
export function readSheetMeta(rows, headerRow) {
    const meta = { supplier: '', supplierCode: '', service: '', fuelSurcharge: '', validFrom: '', validUntil: '', validity: '' };
    if (!(headerRow > 0)) return meta;

    for (const row of (rows || []).slice(0, headerRow)) {
        const label = squash((row || [])[0]);
        const value = String((row || [])[1] ?? '').trim();
        if (!label || !value) continue;
        if (label === 'SUPPLIER') meta.supplier = value;
        else if (label === 'SUPPLIERCODEMONADA' || label === 'SUPPLIERCODE') meta.supplierCode = value;
        else if (label === 'SERVICE') meta.service = value;
        else if (label === 'FUELSURCHARGE') meta.fuelSurcharge = value;
        else if (label === 'VALIDITY') {
            meta.validity = value;
            Object.assign(meta, parseValidity(value));
        }
    }
    return meta;
}

/**
 * Which way the sheet's rates run, when the sheet says so. Most sheets price a suburb both ways
 * (to and from the port at the same price) and use neutral column names; a one-way sheet names
 * its columns for the direction - DELIVERY LOCATION + ORIGIN PORT (port to door, import) or
 * COLLECTION LOCATION + DESTINATION (PORT) (door to port, export) - and often says it in the
 * service line, sheet or file name ("EXPORT", "Ex Auckland"). The aliases above fold those
 * columns into one, so this reads the raw names first. Before, the direction was lost and both
 * were ticked, turning a one-way sheet into two-way rates (Custom Logistic, TGE, 7 Sep 2026).
 *
 * @returns {{ direction: 'import'|'export'|null, reason: string }}
 */
export function sheetDirection({ headerNames = [], meta = {}, sheetName = '', fileName = '' } = {}) {
    const names = headerNames.map(squash);
    const importCols = names.filter(n => n === 'DELIVERYLOCATION' || n === 'ORIGINPORT');
    const exportCols = names.filter(n => n === 'COLLECTIONLOCATION' || n === 'DESTINATIONPORT');
    const label = { DELIVERYLOCATION: 'DELIVERY LOCATION', ORIGINPORT: 'ORIGIN PORT', COLLECTIONLOCATION: 'COLLECTION LOCATION', DESTINATIONPORT: 'DESTINATION (PORT)' };
    if (importCols.length && !exportCols.length) return { direction: 'import', reason: `the sheet's columns are ${importCols.map(n => label[n]).join(' and ')}` };
    if (exportCols.length && !importCols.length) return { direction: 'export', reason: `the sheet's columns are ${exportCols.map(n => label[n]).join(' and ')}` };

    const text = `${meta.service || ''} | ${sheetName || ''} | ${fileName || ''}`.toUpperCase();
    const saysExport = /\bEXPORT\b|\bRETURNS? TO\b|\bFROM ORIGIN TO\b/.test(text);
    const saysImport = /\bIMPORT\b|\bEX [A-Z]/.test(text);
    if (saysExport && !saysImport) return { direction: 'export', reason: 'the sheet says export' };
    if (saysImport && !saysExport) return { direction: 'import', reason: 'the sheet says import ("ex" the port)' };
    return { direction: null, reason: '' };
}

/**
 * The fuel surcharge the sheet states, as a percentage: a FUEL SURCHARGE column with one value
 * for every row (0.27 or 27 -> 27), or a "FUEL SURCHARGE:" line in the header block. Null when
 * the sheet doesn't say, or says different values on different rows.
 */
export function sheetFuelSurchargePct(rows = [], meta = {}) {
    const toPct = (v) => {
        const n = parseFloat(String(v ?? '').replace('%', '').trim());
        if (!Number.isFinite(n) || n < 0) return null;
        return Math.round((n <= 1 ? n * 100 : n) * 100) / 100;
    };
    const values = [...new Set(rows.map(r => toPct(r['FUEL SURCHARGE'])).filter(v => v !== null))];
    if (values.length === 1) return values[0];
    if (values.length > 1) return null;
    return meta.fuelSurcharge ? toPct(meta.fuelSurcharge) : null;
}

/**
 * Turn a sheet read as rows-of-cells into the header-keyed rows convertMetroTariff takes,
 * plus whatever the carrier's header block said.
 *
 * @param {Array[]} rows  the worksheet as an array of arrays (XLSX sheet_to_json header:1)
 * @returns {{ rows: object[], meta: object, headerRow: number, columns: string[] }}
 */
export function readMetroTariffSheet(rows, { sheetName = '', fileName = '' } = {}) {
    const headerRow = findHeaderRow(rows);
    if (headerRow < 0) {
        throw new Error('No POSTCODE column found — is this a metro trucking sheet?');
    }

    const columns = (rows[headerRow] || []).map(canonicalHeader);
    const meta = readSheetMeta(rows, headerRow);

    const out = [];
    for (const row of rows.slice(headerRow + 1)) {
        const record = {};
        let hasValue = false;
        columns.forEach((name, i) => {
            if (!name) return;
            const cell = (row || [])[i];
            record[name] = cell == null ? '' : cell;
            if (String(cell ?? '').trim()) hasValue = true;
        });
        // Carriers leave blank spacer rows and the odd note under the table.
        if (hasValue) out.push(record);
    }

    const { direction, reason } = sheetDirection({ headerNames: rows[headerRow] || [], meta, sheetName, fileName });
    meta.direction = direction;
    meta.directionReason = reason;
    meta.fuelSurchargePct = sheetFuelSurchargePct(out, meta);

    return { rows: out, meta, headerRow, columns: columns.filter(Boolean) };
}
